"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const uuid = __importStar(require("uuid"));
/**
 * @example
 * const config: IWebSocketConfig = {
 *     url: 'socketURL',
 *     queryStringParameters: 'queryParam1=something&queryParam2=somethingelse',
 *     reconnectOnDisconnect: true,
 *     keepAliveIntervalTime: 30000
 * };
 * const saWebSocket = new websocketWrapper(config);
 * @returns {Class} Returns a new instance of the websocketWrapper class.
 */
class WebsocketWrapper {
    constructor(config) {
        this.verifyConfig(config);
        const { url, queryStringParameters = '', reconnectOnDisconnect = false, keepAliveConfig, reconnectionTimeoutTimeMs = 5000, } = config;
        const { shouldSendKeepAlive = false, keepAliveIntervalTimeMs = 9 * 60 * 1000 } = keepAliveConfig || {};
        this.websocketConfig = {
            url: url,
            queryStringParameters,
            reconnectOnDisconnect,
            reconnectionTimeoutTimeMs,
            shouldSendKeepAlive,
            keepAliveInterval: undefined,
            keepAliveIntervalTimeMs,
            listener: document.createElement('customWebSocket'),
            currentListeners: new Map(),
            webSocket: new WebSocket(url + '?' + queryStringParameters),
        };
        this.addBaseListeners();
    }
    verifyConfig(config) {
        const { url } = config;
        if (!url) {
            throw new Error('Error: No url specified');
        }
    }
    reconnect() {
        const { url, queryStringParameters } = this.websocketConfig;
        this.websocketConfig.webSocket = new WebSocket(url + '?' + queryStringParameters);
        this.addBaseListeners();
    }
    addBaseListeners() {
        const { listener, shouldSendKeepAlive, keepAliveIntervalTimeMs, reconnectOnDisconnect, reconnectionTimeoutTimeMs, webSocket, } = this.websocketConfig;
        webSocket.addEventListener('open', () => {
            const connectedEvent = new Event('connect');
            listener.dispatchEvent(connectedEvent);
            // Idle timeouts for AWS WS is 10 minutes, so every 9 minutes or otherwise specified time we will send out a keepAlive event
            // Note if there is no route with the name keepAlive, the default route will be pinged
            if (shouldSendKeepAlive) {
                this.websocketConfig.keepAliveInterval = setInterval(() => {
                    this.send('keepAlive');
                }, keepAliveIntervalTimeMs);
            }
        });
        webSocket.addEventListener('close', data => {
            const disconnectEvent = new CustomEvent('disconnect', { detail: data });
            listener.dispatchEvent(disconnectEvent);
            clearInterval(this.websocketConfig.keepAliveInterval);
            // If reconnectOnDisconnect is true we attempt to reconnect every 5 seconds
            // This is only a timeout, not an interval as everytime we call connect(), if the connection fails this close event is still triggered
            if (reconnectOnDisconnect) {
                setTimeout(() => {
                    this.reconnect();
                }, reconnectionTimeoutTimeMs);
            }
        });
        webSocket.addEventListener('error', data => {
            const errorEvent = new CustomEvent('error', { detail: data });
            listener.dispatchEvent(errorEvent);
        });
        /**
         * This is where all messages from the WS will come to
         * Each message will have a field called action, which maps to a custom event listener we have added
         * We build a new CustomEvent using the action as the type, pass in the rest of the data and dispatch this event onto the listener
         * Dispatching this event, will trigger our custom event listeners callback, wherever that is
         */
        webSocket.addEventListener('message', ({ data }) => {
            const message = JSON.parse(data);
            const dynamicMessageEvent = new CustomEvent(message.action, { detail: message.data });
            listener.dispatchEvent(dynamicMessageEvent);
        });
    }
    get config() {
        return Object.assign({}, this.websocketConfig);
    }
    /**
     * This method can be used to update any of the initial WS parameters passed in.
     * N.B. Updating the url and/or queryStringParameters won't come into effect until you have reconnected.
     * This method is currently limited to only the values that can be passed in on class initialisation.
     * @example
     * console.log(websocket.config.queryStringParameters) // equals ''
     * const newConfig = websocket.updateParameters({ queryStringParameters: 'tokenID=new-tokenID' });
     * console.log(newConfig.queryStringParameters) // equals 'tokenID=new-tokenID'
     * @returns the updated websocket config as readonly.
     */
    updateParameters(parameters) {
        this.websocketConfig = Object.assign(Object.assign({}, this.websocketConfig), parameters);
        return this.config;
    }
    /**
     * This is what is used to send a message back up the WS.
     * The way an AWS WS determines which route to send the message to is via the route selection expression, default being called action which we use.
     * Whether a string or an object is passed in for the data, an object is sent to the WS, to the route with the same name as the action parameter.
     * @example
     * // emits { "action": "agentChat", "data": "Hello good sir" }
     * saWebsocket.send("message", "Hello good sir");
     * @example
     * // emits { "action": "agentChat", "message": "Here's that pic", "file": "https://www.s3.com" }
     * saWebsocket.send("message", { "message": "Here's that pic", "file": "https://www.s3.com" });
     * @returns nothing.
     */
    send(action, data) {
        const { webSocket } = this.websocketConfig;
        if (webSocket.readyState !== WebSocket.OPEN) {
            console.warn(`A send event for ${action} was received while WebSocket is in an incompatible readyState: ${webSocket.readyState}`);
            return;
        }
        let messageToSend;
        if (typeof data === 'object') {
            messageToSend = Object.assign(Object.assign({}, data), { action: action });
        }
        else {
            messageToSend = {
                action,
                data,
            };
        }
        webSocket.send(JSON.stringify(messageToSend));
    }
    /**
     * This method is called to add a custom event listener or listen to a base event.
     * There are three base events that should be set-up, connect, disconnect and error.
     * @example
     * // BASE: The connect event has no data associated with it
     * saWebSocket.addListener('connect', () => {
     *    console.log('Connected to WS!');
     * });
     * @example
     * // BASE: The disconnect event has an optional code and reason
     * saWebSocket.addListener('disconnect', data => {
     *    const { code, reason } = data;
     *    console.log(`${code}: ${reason}`);
     * });
     * @example
     * // BASE: The error event spits out an error
     * saWebSocket.addListener('error', (data) => {
     *    console.error(data);
     * });
     * @example
     * // An example of a custom event, the listenerID should be stored
     * const listenerID = saWebSocket.addListener('realtimeMetrics', data => {
     *    console.log('Data for realtime metrics received');
     * });
     * @returns {string} Returns an ID that maps to the action and callback.
     */
    addListener(action, callback) {
        const { listener, currentListeners } = this.websocketConfig;
        listener.addEventListener(action, callback);
        const listenerID = uuid.v4();
        currentListeners.set(listenerID, { action, callback });
        return listenerID;
    }
    /**
     * This method is called to remove a custom event listener.
     * This cannot be used to remove a base event.
     * @example
     * useEffect(() => {
     *     const listenerID = saWebSocket.addListener('realtimeMetrics', data => {
     *        console.log('Data for realtime metrics received');
     *     });
     *
     *     return (() => {
     *         saWebSocket.removeListener(listenerID);
     *     });
     * });
     * @returns {none} Returns nothing.
     */
    removeListener(listenerID) {
        const { currentListeners, listener } = this.websocketConfig;
        const { action, callback } = currentListeners.get(listenerID) || {};
        if (!action || !callback)
            return false;
        listener.removeEventListener(action, callback, false);
        return currentListeners.delete(listenerID);
    }
}
exports.default = WebsocketWrapper;
